import React, { useState, useCallback, useMemo } from 'react';
import { useMailProvider } from './MailProviderContext';
import { useMailOpenStatusByThread } from '../../lib/hooks/useMailOpenStatus';
import { MailThreadListItem, FLUSIA_GRADIENT } from './MailThreadListItem';
import { MailLinkEntityDrawer } from './MailLinkEntityDrawer';
import { MailNavMenu } from './MailNavMenu';
import { partitionThreadsForList } from '../../lib/stores/mail/helpers';
import {
  Inbox,
  Send,
  Star,
  Mail,
  Search,
  RefreshCw,
  Loader,
  PenSquare,
  MailOpen,
  PanelRight,
  List,
  ChevronDown,
  Link2,
  X,
  Archive,
  Trash2,
  Tag,
  CheckSquare,
  PanelLeft,
} from 'lucide-react';

interface MailSidebarProps {
  organizationId: string;
  userId: string;
}

const LABELS = [
  { id: 'INBOX', label: 'Inbox', icon: Inbox },
  { id: 'SENT', label: 'Inviati', icon: Send },
  { id: 'STARRED', label: 'Preferiti', icon: Star },
  { id: 'ALL', label: 'Tutti', icon: Mail },
];

export function MailSidebar({ organizationId, userId }: MailSidebarProps) {
  const { useStore, displayName } = useMailProvider();
  const {
    threads,
    threadsLoading,
    threadsError,
    activeLabel,
    searchQuery,
    selectedThreadId,
    syncStatus,
    viewMode,
    setViewMode,
    setActiveLabel,
    setSearchQuery,
    loadThreads,
    searchThreads,
    selectThread,
    syncInbox,
    setComposing,
    archiveThread,
    trashThread,
    markAsRead,
    markAsUnread,
    setThreadLabel,
    labels,
    keptUnreadThreadIds,
    clearKeptUnread,
  } = useStore();

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [showBulkLabels, setShowBulkLabels] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const fullWidth = viewMode === 'list';
  const userLabels = useMemo(() => Object.values(labels).filter(l => l.type === 'user'), [labels]);

  // Stato apertura (tracking pixel) aggregato per thread — una query per l'intera lista
  const openByThread = useMailOpenStatusByThread(useMemo(() => threads.map(t => t.thread_id), [threads]));

  // Multi-selezione thread per collegamento in blocco
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showLinkDrawer, setShowLinkDrawer] = useState(false);
  const toggleSelect = useCallback((threadId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId); else next.add(threadId);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const selectedThreads = useMemo(
    () => threads.filter(t => selectedIds.has(t.thread_id)).map(t => ({ threadId: t.thread_id, messages: t.messages })),
    [threads, selectedIds]
  );

  const allSelected = threads.length > 0 && selectedIds.size === threads.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(threads.map(t => t.thread_id)));

  // Azioni bulk sui thread selezionati
  const runBulk = async (fn: (id: string) => Promise<unknown>) => {
    const ids = [...selectedIds];
    await Promise.all(ids.map(fn));
    clearSelection();
  };
  const msgIds = (id: string) => (threads.find(t => t.thread_id === id)?.messages || []).map(m => m.id);
  const bulkArchive = () => runBulk(id => archiveThread(organizationId, userId, id));
  const bulkTrash = () => runBulk(id => trashThread(organizationId, userId, id));
  const bulkRead = () => runBulk(id => markAsRead(organizationId, userId, msgIds(id)));
  const bulkUnread = () => runBulk(id => markAsUnread(organizationId, userId, msgIds(id)));
  const bulkLabel = (labelId: string) => { setShowBulkLabels(false); return runBulk(id => setThreadLabel(organizationId, userId, id, labelId, true)); };

  const handleLabelChange = useCallback((label: string) => {
    setActiveLabel(label);
    setSearchQuery('');
    setLocalSearch('');
    loadThreads(organizationId, userId, label);
  }, [organizationId, userId, setActiveLabel, setSearchQuery, loadThreads]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    searchThreads(organizationId, userId, localSearch);
  }, [organizationId, userId, localSearch, searchThreads]);

  // Sincronizza a mano = ricarica della vista: le conversazioni gia` lette lasciano "Da leggere"
  const handleSync = useCallback(() => {
    clearKeptUnread();
    syncInbox(organizationId, userId, false);
  }, [organizationId, userId, syncInbox, clearKeptUnread]);

  return (
    <div className="h-full flex">
      {showNav && <MailNavMenu organizationId={organizationId} userId={userId} onClose={() => setShowNav(false)} />}
      <div className="flex-1 min-w-0 flex flex-col bg-white dark:bg-gray-950">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-800">
        <h2 className="text-base font-semibold text-[#37352F] dark:text-white flex items-center gap-2">
          <button onClick={() => setShowNav(v => !v)} className="p-1 -ml-1 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] dark:text-white/60" title="Menu">
            <PanelLeft className="h-4 w-4" />
          </button>
          {displayName}
        </h2>
        <div className="flex items-center gap-1">
          {/* Toggle vista: anteprima a destra / righe lunghe */}
          <button
            onClick={() => setViewMode(viewMode === 'split' ? 'list' : 'split')}
            className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] dark:text-white/60"
            title={viewMode === 'split' ? 'Vista a righe lunghe' : 'Vista con anteprima a destra'}
          >
            {viewMode === 'split' ? <List className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
          </button>
          <button
            onClick={handleSync}
            disabled={syncStatus === 'syncing'}
            className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] dark:text-white/60 disabled:opacity-50"
            title="Sincronizza"
          >
            {syncStatus === 'syncing' ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => setComposing(true)}
            className="p-1.5 rounded-md text-white shadow-sm hover:opacity-90 transition-opacity"
            style={{ background: FLUSIA_GRADIENT }}
            title="Nuova email"
          >
            <PenSquare className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#787774]" />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder="Cerca email..."
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border-none rounded-md focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white placeholder-[#787774]"
          />
        </div>
      </form>

      {/* Barra azioni multi-selezione (stile Gmail) */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-0.5 px-2 py-1.5 bg-[#2383E2]/8 border-y border-[#2383E2]/20">
          <button onClick={toggleSelectAll} className="p-1.5 rounded text-[#2383E2] hover:bg-black/5 dark:hover:bg-white/10" title={allSelected ? 'Deseleziona tutto' : 'Seleziona tutto'}>
            <CheckSquare className="h-4 w-4" />
          </button>
          <span className="text-xs font-semibold text-[#2383E2] mr-1">{selectedIds.size}</span>
          <BulkBtn icon={<Archive className="h-4 w-4" />} title="Archivia" onClick={bulkArchive} />
          <BulkBtn icon={<MailOpen className="h-4 w-4" />} title="Segna come letto" onClick={bulkRead} />
          <BulkBtn icon={<Mail className="h-4 w-4" />} title="Segna come non letto" onClick={bulkUnread} />
          <div className="relative">
            <BulkBtn icon={<Tag className="h-4 w-4" />} title="Applica etichetta" onClick={() => setShowBulkLabels(v => !v)} />
            {showBulkLabels && (
              <div className="absolute top-full left-0 mt-1 w-52 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-30">
                {userLabels.length === 0 && <div className="px-3 py-2 text-xs text-[#787774]">Nessuna etichetta</div>}
                {userLabels.map(l => (
                  <button key={l.id} onClick={() => bulkLabel(l.id)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: l.backgroundColor || '#9B9A97' }} />
                    <span className="truncate">{l.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <BulkBtn icon={<Trash2 className="h-4 w-4" />} title="Cestina" onClick={bulkTrash} danger />
          <div className="flex-1" />
          <button onClick={() => setShowLinkDrawer(true)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-md" style={{ background: FLUSIA_GRADIENT }} title="Collega a CRM">
            <Link2 className="h-3.5 w-3.5" /> Collega
          </button>
          <button onClick={clearSelection} className="p-1.5 rounded text-[#787774] hover:bg-black/5 dark:hover:bg-white/10" title="Annulla selezione">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Labels */}
      <div className="flex gap-1 px-3 pb-2">
        {LABELS.map(({ id, label, icon: Icon }) => {
          const isActive = activeLabel === id;
          return (
            <button
              key={id}
              onClick={() => handleLabelChange(id)}
              style={isActive ? { background: FLUSIA_GRADIENT } : undefined}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
                isActive
                  ? 'text-white shadow-sm'
                  : 'text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'
              }`}
            >
              <Icon className="h-3 w-3" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {threadsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-5 w-5 animate-spin text-[#787774]" />
          </div>
        ) : threads.length === 0 ? (
          // Lista vuota: distingue l'errore di caricamento dal genuino "nessuna email"
          // (prima l'errore era invisibile e l'utente vedeva un vuoto silenzioso).
          <div className="flex flex-col items-center justify-center py-12 text-[#787774]">
            <MailOpen className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm">{threadsError ? 'Errore nel caricamento delle email' : 'Nessuna email trovata'}</p>
            {threadsError && (
              <button
                onClick={() => loadThreads(organizationId, userId)}
                className="mt-2 text-xs text-[#2383E2] hover:underline"
              >
                Riprova
              </button>
            )}
          </div>
        ) : (
          (() => {
            // Le conversazioni aperte restano in "Da leggere" fino al ricaricamento della vista
            const { unread, read, unreadCount } = partitionThreadsForList(threads, keptUnreadThreadIds);
            const renderItem = (thread: typeof threads[number]) => (
              <MailThreadListItem
                key={thread.thread_id}
                thread={thread}
                isSelected={selectedThreadId === thread.thread_id}
                organizationId={organizationId}
                userId={userId}
                fullWidth={fullWidth}
                onClick={() => selectThread(thread.thread_id, organizationId, userId)}
                checked={selectedIds.has(thread.thread_id)}
                selectionActive={selectedIds.size > 0}
                onToggleCheck={(e) => { e.stopPropagation(); toggleSelect(thread.thread_id); }}
                openInfo={openByThread.get(thread.thread_id)}
              />
            );
            return (
              <>
                {unread.length > 0 && (
                  <>
                    <SectionHeader label="Da leggere" count={unreadCount} />
                    {unread.map(renderItem)}
                  </>
                )}
                {read.length > 0 && (
                  <>
                    <SectionHeader label={unread.length > 0 ? 'Tutto il resto' : 'Tutte'} count={read.length} />
                    {read.map(renderItem)}
                  </>
                )}
              </>
            );
          })()
        )}
      </div>

      {/* Drawer collegamento multiplo */}
      <MailLinkEntityDrawer
        isOpen={showLinkDrawer}
        onClose={() => { setShowLinkDrawer(false); clearSelection(); }}
        threads={selectedThreads}
        organizationId={organizationId}
        userId={userId}
      />
      </div>
    </div>
  );
}

function BulkBtn({ icon, title, onClick, danger }: { icon: React.ReactNode; title: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${danger ? 'text-[#787774] hover:text-[#DC2626] hover:bg-[#DC2626]/10' : 'text-[#787774] dark:text-white/60 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10'}`}
    >
      {icon}
    </button>
  );
}

function SectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-1.5 px-4 py-1.5 bg-[#FBFBFA]/95 dark:bg-gray-950/95 backdrop-blur border-b border-[rgba(55,53,47,0.06)] dark:border-gray-800/50">
      <ChevronDown className="h-3 w-3 text-[#9B9A97]" />
      <span className="text-[11px] font-semibold text-[#787774] dark:text-white/50">{label}</span>
      <span className="text-[11px] text-[#9B9A97] dark:text-white/30">{count}</span>
    </div>
  );
}
